import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukDayYear } from "@/lib/time";
import { Btn, Tagk } from "@/components/portal/crm/kit";

const BRAND = ["#0055FF", "#7DA9FF", "#0B6B4F", "#2DD4BF", "#0B1220"];

/* Brand burst — pieces from both bottom corners for ~3 seconds, then it is over. */
function burst(canvas) {
  const fire = confetti.create(canvas, { resize: true, useWorker: false });
  const end = Date.now() + 3000;
  const tick = () => {
    const left = end - Date.now();
    if (left <= 0) return;
    const base = { particleCount: 4, spread: 62, startVelocity: 58, ticks: 240, gravity: 0.95, scalar: 1.05, colors: BRAND };
    fire({ ...base, angle: 60, origin: { x: 0, y: 1 } });
    fire({ ...base, angle: 120, origin: { x: 1, y: 1 } });
    requestAnimationFrame(tick);
  };
  fire({ particleCount: 90, spread: 100, startVelocity: 48, origin: { x: 0.5, y: 0.7 }, colors: BRAND, ticks: 260 });
  tick();
  return () => fire.reset();
}

/** An agent's first case has funded — confetti and a card, once. Dismissing stamps it seen. */
export function FirstFundedCelebration({ celebration, me, onDone }) {
  const nav = useNavigate();
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    return burst(canvasRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = async (then) => {
    onDone?.();
    try { await api.post("/refer/seats/me/celebration-seen"); } catch { /* it shows again next visit, which is fine */ }
    if (then) nav(then);
  };

  const first = (me?.seat?.name || "").trim().split(" ")[0] || "there";
  const company = me?.company || me?.name || "your company";

  return (
    <div className="cxp" role="dialog" aria-modal="true" aria-labelledby="first-funded-title" data-testid="first-funded-celebration"
         style={{ position: "fixed", inset: 0, zIndex: 120, display: "grid", placeItems: "center", padding: 20,
                  background: "rgba(11,18,32,.58)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
      <canvas ref={canvasRef} aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
      <div className="card" data-testid="first-funded-card"
           style={{ position: "relative", width: "min(560px, 100%)", padding: "30px 32px 28px", borderRadius: 18,
                    boxShadow: "0 30px 80px rgba(11,18,32,.45), 0 0 0 1px rgba(125,169,255,.35)",
                    animation: "ffc-in .5s cubic-bezier(.2,.8,.2,1) both" }}>
        <style>{`@keyframes ffc-in{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}`}</style>
        <Tagk>Connexis · First case funded</Tagk>
        <h2 id="first-funded-title" className="card-t" data-testid="first-funded-title"
            style={{ font: "800 30px/1.1 Archivo", letterSpacing: "-.02em", margin: "14px 0 10px", color: "var(--ink)" }}>
          Your first funded case, {first}!
        </h2>
        <p style={{ margin: "0 0 20px", color: "var(--ink-3)", fontSize: 14.5, lineHeight: 1.55 }}>
          The {(celebration.finance_type || "business funding").toLowerCase()} case you put in for <b>{celebration.business_name || "your client"}</b> has
          gone all the way through. You found the business and got the application in clean; the desk did the chasing; the funder said yes.
          Most cases never make it this far.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1, background: "var(--line)",
                      border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden", marginBottom: 22 }} data-testid="first-funded-facts">
          {[
            ["Case", celebration.reference || "—"],
            ["Size", celebration.amount_gbp ? gbp(celebration.amount_gbp) : "—"],
            ["Funded", ukDayYear(celebration.funded_at || celebration.at, "today")],
          ].map(([k, v]) => (
            <div key={k} style={{ background: "var(--card)", padding: "12px 14px", minWidth: 0 }}>
              <div className="rail-t" style={{ marginBottom: 4 }}>{k}</div>
              <div style={{ font: "700 15px/1.2 'Instrument Sans'", color: "var(--ink)", fontVariantNumeric: "tabular-nums",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
            </div>
          ))}
        </div>

        <p style={{ margin: "0 0 22px", color: "var(--mut)", fontSize: 12.5, lineHeight: 1.5 }}>
          It reads <b style={{ color: "var(--green)" }}>Funded</b> on your cases now. Commission and the statement sit with {company}, as always.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Btn onClick={() => close()} testId="first-funded-close">Back to my cases</Btn>
          <Btn kind="primary" onClick={() => close("/introducer/submit")} testId="first-funded-next">Submit the next one</Btn>
        </div>
      </div>
    </div>
  );
}
